import { useHandler } from "@chatbotgang/etude/react/useHandler";
import {
  type DialogueId,
  DialogueIdSchema,
  type WebChatConfig,
  WebChatConfigSchema,
} from "@chatbotgang/web-sdk-core/model";
import { ClSdkDefaultUi } from "@chatbotgang/web-sdk-default-ui";
import type { Dialogue } from "@chatbotgang/web-sdk-default-ui/model/index";
import { css } from "@emotion/react";
import { en, Faker } from "@faker-js/faker";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import ButtonGroup from "@mui/material/ButtonGroup";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { theme } from "@polifonia/theme";
import { getRouteApi, Link, Navigate } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { Suspense, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";

import { Button } from "@/components/Button";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Typography } from "@/components/Typography";
import { useEnableWccsAutoreply } from "@/features/web-chat/useEnableWccsAutoreply";
import { useEnableWebChatBrandConfig } from "@/features/web-chat/useEnableWebChatBrandConfig";
import { interlude } from "@/interlude";
import { defineStyles } from "@/internal/emotion";
import { PageErrorBoundary } from "@/layout/ErrorBoundary/PageErrorBoundary";
import { useWebChatConfigForm } from "@/pages/channels/hooks/form/useWebChatConfigForm";
import {
  ChannelQueriesContext,
  ChannelQueriesContextProvider,
} from "@/pages/channels/pages/detail/channelQueriesContext";
import carouselPlaceholder from "@/pages/channels/pages/detail/wccs/image/carouselPlaceholder.jpg";
import { BrandIdentityForm } from "@/pages/channels/pages/detail/wccs/WebChatConfig/BrandIdentityForm";
import { FooterForm } from "@/pages/channels/pages/detail/wccs/WebChatConfig/FooterForm";
import { LauncherForm } from "@/pages/channels/pages/detail/wccs/WebChatConfig/LauncherForm";
import { TriggersForm } from "@/pages/channels/pages/detail/wccs/WebChatConfig/TriggersForm";
import { WelcomeMessagesForm } from "@/pages/channels/pages/detail/wccs/WebChatConfig/WelcomeMessagesForm";
import { PageTitle } from "@/pages/components/PageTitle";

const route = getRouteApi("/channels/wccs/$id/web-chat-config");
const faker = new Faker({ locale: en });

const PREVIEW_FIXED_SPACING_PX = 24;
const PREVIEW_MOBILE_MAX_WIDTH = 600;
const PREVIEW_DESKTOP_MAX_WIDTH = 400;

const styles = defineStyles({
  link: css({
    textDecoration: "none",
  }),
  pageTitle: css({
    marginBottom: 0,
  }),
  appearanceTitle: css({
    fontWeight: 700,
  }),
  scrollContainer: css({
    overflowY: "auto",
    maxHeight: "calc(100vh - 200px)",
  }),
  formContainer: css({
    flexGrow: 1,
    flexShrink: 0,
  }),
  previewContainer: css({
    position: "relative",
    width: "484px",
    height: "560px",
  }),
  previewTitle: css({
    fontWeight: 700,
  }),
});

const LocaleKeyEnum = z.enum(["en-US", "zh-TW", "ja", "th"]);
const ModeEnum = z.enum(["desktop", "mobile"]);

namespace WebChatConfigPage {
  export type LocaleKey = z.infer<typeof LocaleKeyEnum>;
  export type Mode = z.infer<typeof ModeEnum>;
}

const WebChatConfigInternal: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = route.useParams();
  const { channel } = ChannelQueriesContext.useData();
  /**
   * The page answers to two independent permissions. The brand config
   * sections need the Statsig gate; the Footer section needs the paid
   * capability. Interlude applies each one again when it builds what the
   * widget receives, so a value saved without its permission never reaches
   * the site.
   */
  const canEditAppearance = useEnableWebChatBrandConfig();
  /**
   * Suspense rather than the plain query: an organization with only the
   * footer capability would otherwise read `false` on the first render and be
   * redirected before the setting loads.
   */
  const orgSettingQuery =
    interlude.organization.useSuspenseOrganizationSetting();
  const canEditFooter = orgSettingQuery.data.wccsCustomChatFooterEnabled;
  const enableWccsAutoreply = useEnableWccsAutoreply();
  const webChatConfigFormMethod = useWebChatConfigForm();
  /**
   * Kept out of the brand config form: the zod resolver would strip the key
   * on submit, and the choice is stored on the channel, not in
   * `webChatConfig`. With the capability on, interlude returns the stored
   * choice as is, so the channel value is the draft's starting point.
   */
  const [footerDisabledDraft, setFooterDisabledDraft] = useState(
    channel.type === "wccs" && channel.channelInformation.webChatFooterDisabled,
  );
  const updateChannelMutation = interlude.channel.useUpdateChannel(
    {
      params: {
        channelId: channel.id,
      },
    },
    {
      onSuccess: () => {
        enqueueSnackbar(t("common.updatedSuccessfully"), {
          variant: "success",
        });
      },
    },
  );
  const [selectedDialogueId, setSelectedDialogueId] = useState<
    DialogueId | undefined
  >(undefined);
  const [previewMode, setPreviewMode] = useState<"start" | "chat">("start");
  const [selectedLocale, setSelectedLocale] =
    useState<WebChatConfigPage.LocaleKey>("en-US");
  const [selectedMode, setSelectedMode] =
    useState<WebChatConfigPage.Mode>("desktop");
  const webChatConfigFormValues = webChatConfigFormMethod.watch();

  /**
   * Runs the form value through `WebChatConfigSchema` before the preview
   * uses it. react-hook-form takes its value type from the resolver's
   * *input* side, where two fields are `unknown` by design:
   * `welcomeMessages` goes through a `z.preprocess` that normalizes camelCase
   * locale keys, and `webChatTrigger.displayFilter` ends in `.catch(null)`.
   * Both declare that they accept anything, so no `useForm` generic recovers
   * the shape -- `watch()` returns the input side whatever the third generic
   * says.
   *
   * Running the schema buys back what an assertion only claimed: if
   * `WebChatConfigSchema` stops matching what the form holds, this throws
   * into `PageErrorBoundary` instead of the preview silently rendering the
   * wrong thing.
   *
   * `parse` rather than `safeParse`, because no field of this form can reach
   * a state the schema rejects, so a throw means the schema and the form have
   * genuinely diverged and the loud failure is the point. The fields that
   * could otherwise fail are all narrowed by their inputs: `NumberInput` only
   * calls `onChange` with a real number and resolves an emptied box to its
   * `min` on blur, and the three url fields (`brandIdentity.logoUrl`,
   * `brandIdentity.serviceAvatarUrl`, `launcher.widgetImageUrl`) are upload
   * widgets whose clear button writes the default url back. Colors and
   * message text are plain `z.string()`, and `displayFilter` has its own
   * `.catch(null)`.
   *
   * `WebChatConfigSchema` rather than the form's
   * `WebChatConfigSchemaWithValidation`: the extra `superRefine` there raises
   * user-facing field errors, which say nothing about whether the shape is
   * renderable.
   *
   * Spacing is pinned so the preview's layout does not move while the tenant
   * edits the real launcher offsets.
   *
   * Without the appearance permission, interlude sends the widget no brand
   * config at all, so the site draws the SDK's built-in defaults. The preview
   * passes only the pinned spacing to match that, rather than the stored
   * config or `defaultWebChatConfig`, neither of which the site shows.
   */
  const previewChatConfig: React.ComponentProps<
    typeof ClSdkDefaultUi
  >["chatConfig"] = useMemo(() => {
    if (!canEditAppearance) {
      return {
        launcher: {
          desktopSetting: {
            bottomSpacingPx: PREVIEW_FIXED_SPACING_PX,
            inlineSpacingPx: PREVIEW_FIXED_SPACING_PX,
          },
          mobileSetting: {
            bottomSpacingPx: PREVIEW_FIXED_SPACING_PX,
            inlineSpacingPx: PREVIEW_FIXED_SPACING_PX,
          },
        },
      };
    }

    const chatConfig = WebChatConfigSchema.parse(webChatConfigFormValues);

    return {
      ...chatConfig,
      launcher: {
        ...chatConfig.launcher,
        desktopSetting: {
          ...chatConfig.launcher.desktopSetting,
          bottomSpacingPx: PREVIEW_FIXED_SPACING_PX,
          inlineSpacingPx: PREVIEW_FIXED_SPACING_PX,
        },
        mobileSetting: {
          ...chatConfig.launcher.mobileSetting,
          bottomSpacingPx: PREVIEW_FIXED_SPACING_PX,
          inlineSpacingPx: PREVIEW_FIXED_SPACING_PX,
        },
      },
    };
  }, [canEditAppearance, webChatConfigFormValues]);

  const dialogueId = useMemo(
    () => DialogueIdSchema.parse(faker.string.uuid()),
    [],
  );

  const dialogues: Array<Dialogue> = useMemo(
    () => [
      {
        id: dialogueId,
        messages: [
          {
            id: faker.string.uuid(),
            dialogueId,
            type: "plain_text",
            content: t("webChatConfig.preview.sampleMessage.sender"),
            role: "sender",
            multimedia: null,
            createdAt: new Date(),
            readAt: new Date(),
          },
          {
            id: faker.string.uuid(),
            dialogueId,
            type: "plain_text",
            content: t("webChatConfig.preview.sampleMessage.receiver"),
            role: "receiver",
            multimedia: null,
            createdAt: new Date(),
            readAt: new Date(),
          },
          ...(enableWccsAutoreply
            ? [
                {
                  id: faker.string.uuid(),
                  dialogueId,
                  type: "carousel" as const,
                  content: "",
                  role: "receiver" as const,
                  multimedia: null,
                  createdAt: new Date(),
                  readAt: new Date(),
                  detail: [
                    {
                      title: t("webChatConfig.preview.carousel.cardTitle"),
                      description: t(
                        "webChatConfig.preview.carousel.cardDescription",
                      ),
                      imageUrl: carouselPlaceholder,
                      actions: [
                        {
                          type: "send_message" as const,
                          label: t("webChatConfig.preview.carousel.button1"),
                          message: t("webChatConfig.preview.carousel.message1"),
                          tags: [],
                        },
                        {
                          type: "send_message" as const,
                          label: t("webChatConfig.preview.carousel.button2"),
                          message: t("webChatConfig.preview.carousel.message2"),
                          tags: [],
                        },
                        {
                          type: "open_url" as const,
                          label: t("webChatConfig.preview.carousel.button3"),
                          url: "https://example.com",
                          tags: [],
                        },
                      ],
                    },
                    {
                      quickReply: {
                        items: [
                          {
                            action: {
                              type: "send_message" as const,
                              message: t(
                                "webChatConfig.preview.carousel.message1",
                              ),
                            },
                            label: t("webChatConfig.preview.carousel.button1"),
                          },
                          {
                            action: {
                              type: "send_message" as const,
                              message: t(
                                "webChatConfig.preview.carousel.message2",
                              ),
                            },
                            label: t("webChatConfig.preview.carousel.button2"),
                          },
                          {
                            action: {
                              type: "open_url" as const,
                              url: "https://example.com",
                            },
                            label: t("webChatConfig.preview.carousel.button3"),
                          },
                        ],
                      },
                    },
                  ],
                } satisfies Dialogue["messages"][number],
              ]
            : []),
        ],
      },
    ],
    [dialogueId, enableWccsAutoreply, t],
  );

  /**
   * One request carries every field the organization may edit, and only
   * those. Leaving `webChatConfig` out without the appearance permission
   * matters: the form holds the stored config or the defaults, and sending
   * it would overwrite the customer's stored brand config.
   */
  const handleAppearanceSubmit: Parameters<
    typeof webChatConfigFormMethod.handleSubmit
  >[0] = useHandler((webChatConfig: WebChatConfig) => {
    updateChannelMutation.mutate({
      channelInformation: {
        webChatConfig,
        ...(canEditFooter && { webChatFooterDisabled: footerDisabledDraft }),
      },
    });
  });

  /**
   * Skips `handleSubmit`: its validation covers the brand config fields, which
   * this organization cannot see, so an error there would block the save
   * with nothing on screen to fix.
   */
  const handleFooterOnlySubmit = useHandler<
    React.FormEventHandler<HTMLFormElement>
  >((event) => {
    event.preventDefault();
    updateChannelMutation.mutate({
      channelInformation: {
        webChatFooterDisabled: footerDisabledDraft,
      },
    });
  });

  if (channel.type !== "wccs" || (!canEditAppearance && !canEditFooter)) {
    return <Navigate to="/channels" replace />;
  }

  return (
    <Stack gap="24px">
      <Breadcrumbs aria-label="breadcrumb" separator=">">
        <Link to="/channels" css={styles.link}>
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t("channels.title")}
          </Typography>
        </Link>
        <Link
          to="/channels/$type/$id"
          params={{
            type: "wccs",
            id,
          }}
          css={styles.link}
        >
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t("page.channel.breadcrumb.wccs")}
          </Typography>
        </Link>
        <Typography variant="body" color={theme.colors.staticFgNote}>
          {t("webChatConfig.title")}
        </Typography>
      </Breadcrumbs>
      <PageTitle css={styles.pageTitle}>{t("webChatConfig.title")}</PageTitle>
      <Stack direction="row" gap="32px">
        <Box css={styles.formContainer}>
          <FormProvider {...webChatConfigFormMethod}>
            <form
              onSubmit={
                canEditAppearance
                  ? webChatConfigFormMethod.handleSubmit(handleAppearanceSubmit)
                  : handleFooterOnlySubmit
              }
            >
              <Stack direction="column" justifyContent="center" gap="16px">
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h2" css={styles.appearanceTitle}>
                    {t("webChatConfig.appearance.title")}
                  </Typography>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={updateChannelMutation.isPending}
                    disabled={updateChannelMutation.isPending}
                  >
                    {t("webChatConfig.saveChanges")}
                  </Button>
                </Stack>
                <Box css={styles.scrollContainer}>
                  {!canEditAppearance ? null : (
                    <>
                      <BrandIdentityForm />
                      <WelcomeMessagesForm
                        selectedLocale={selectedLocale}
                        setSelectedLocale={setSelectedLocale}
                      />
                      <LauncherForm />
                      <TriggersForm />
                    </>
                  )}
                  {!canEditFooter ? null : (
                    <FooterForm
                      footerDisabled={footerDisabledDraft}
                      onFooterDisabledChange={setFooterDisabledDraft}
                      disabled={updateChannelMutation.isPending}
                      defaultExpanded={!canEditAppearance}
                    />
                  )}
                </Box>
              </Stack>
            </form>
          </FormProvider>
        </Box>
        <Stack gap="16px">
          <Typography variant="h2" css={styles.previewTitle}>
            {t("webChatConfig.preview.title")}
          </Typography>
          <Tabs
            value={selectedMode}
            onChange={(_, newValue) => setSelectedMode(newValue)}
          >
            <Tab value="desktop" label={t("webChatConfig.launcher.desktop")} />
            <Tab value="mobile" label={t("webChatConfig.launcher.mobile")} />
          </Tabs>
          <ButtonGroup fullWidth>
            <Button
              fullWidth
              variant={previewMode === "start" ? "primary" : "secondary"}
              onClick={() => {
                setPreviewMode("start");
                setSelectedDialogueId(undefined);
              }}
            >
              {t("webChatConfig.preview.startScreen")}
            </Button>
            <Button
              fullWidth
              variant={previewMode === "chat" ? "primary" : "secondary"}
              onClick={() => {
                setPreviewMode("chat");
                setSelectedDialogueId(dialogueId);
              }}
            >
              {t("webChatConfig.preview.chatScreen")}
            </Button>
          </ButtonGroup>
          <Box css={styles.previewContainer}>
            <ClSdkDefaultUi
              /**
               * Mind the double negative: `footer` means "show the footer",
               * so hiding it is `false`. `webChatFooterDisabled` is not part
               * of the brand config form, so it goes through `features` and
               * never through `previewChatConfig`, which is saved back into
               * the customer's brand config. See the field in
               * `packages/interlude/models.ts`.
               *
               * Reads the unsaved draft so the switch shows its effect at
               * once. Without the capability the site always shows the
               * footer, whatever the channel stored, and so does the preview.
               */
              features={{
                clChat:
                  canEditFooter && footerDisabledDraft
                    ? { footer: false }
                    : true,
              }}
              chatOpened={true}
              chatConfig={previewChatConfig}
              dialogues={!selectedDialogueId ? [] : dialogues}
              selectedDialogueId={selectedDialogueId}
              autoFocusChatInput={false}
              maxNarrowWidth={
                selectedMode === "mobile"
                  ? PREVIEW_MOBILE_MAX_WIDTH
                  : PREVIEW_DESKTOP_MAX_WIDTH
              }
              i18n={{
                initialOptions: {
                  lng: selectedLocale,
                },
              }}
            />
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};

const WebChatConfigPage: React.FC = () => {
  return (
    <PageErrorBoundary>
      <Suspense fallback={<BarLoading />}>
        <ChannelQueriesContextProvider>
          <WebChatConfigInternal />
        </ChannelQueriesContextProvider>
      </Suspense>
    </PageErrorBoundary>
  );
};

export { LocaleKeyEnum, ModeEnum, WebChatConfigPage };
